import React, { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "sonner";
import {
    Users,
    PlusCircle,
    BookOpen,
    X,
    ArrowLeft,
    Layers,
    Calendar,
    Search,
    MoreVertical,
    Crown,
    CheckCircle,
    Calendar as CalendarIcon,
    Clock,
    FileText,
    ChevronDown,
    ChevronUp,
    ExternalLink,
    XCircle,
    Loader2,
} from "lucide-react";
import { API_BASE_URL } from "../../config/apiConfig";
import { useNavigate } from "react-router-dom";

export default function GroupExamDashboard() {
    const [groups, setGroups] = useState([]);
    const [users, setUsers] = useState([]);
    const [exams, setExams] = useState([]);
    const [currentUser, setCurrentUser] = useState(null);

    // UI State
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [groupName, setGroupName] = useState("");
    const [groupDescription, setGroupDescription] = useState("");
    const [filterStatus, setFilterStatus] = useState("all");
    const [viewMode, setViewMode] = useState("grid");

    // Modal States
    const [showGroupModal, setShowGroupModal] = useState(false);
    const [showExamModal, setShowExamModal] = useState(false);
    const [showMemberModal, setShowMemberModal] = useState(false);
    const [editingGroup, setEditingGroup] = useState(null);

    const [selectedGroup, setSelectedGroup] = useState(null);

    // Animation states
    const [isLoading, setIsLoading] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);

    const navigate = useNavigate();
    const token = useMemo(() => localStorage.getItem('token'), []);

    const filteredGroups = groups.filter((group) => {
        if (filterStatus === "all") return true;
        if (filterStatus === "active") return group.status === "accepted";
        if (filterStatus === "inactive") return group.status !== "accepted"; // pending or rejected
        return true;
    });


    // Memoized current date
    const now = useMemo(() => new Date(), []);

    useEffect(() => {
        fetchGroups();
        fetchUsers();
        fetchCurrentUser();
        fetchAllExams();
    }, []);

    const fetchCurrentUser = useCallback(async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/api/users/me`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const data = await res.json();
            if (res.ok) setCurrentUser(data);
            else console.error(data.message);
        } catch (err) {
            console.error("Error fetching current user:", err);
        }
    }, [token]);

    const fetchGroups = useCallback(async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/api/groups/my-groups`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const data = await res.json();
            if (res.ok) setGroups(data.groups);
            else console.error(data.message);
        } catch (err) {
            console.error("Error fetching groups:", err);
        }
    }, [token]);

    console.log(groups)

    const fetchAllExams = useCallback(async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/api/group-exams/user/exams-all`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json"
                },
            });

            const data = await res.json();

            if (res.ok) {
                setExams(data.allExams);
            } else {
                console.error("Server responded with an error:", data.message);
            }
        } catch (err) {
            console.error("Error fetching exams:", err);
        }
    }, [token]);

    const fetchUsers = useCallback(async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/api/users`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const data = await res.json();
            if (res.ok) setUsers(data.users);
            else console.error(data.message);
        } catch (err) {
            console.error("Error fetching users:", err);
        }
    }, [token]);

    const handleCreateGroup = async () => {
        if (!groupName.trim()) {
            toast.error("Group name is required");
            return;
        }

        setIsLoading(true);
        try {
            const payload = {
                groupName,
                description: groupDescription,
                memberIds: selectedUsers.map((u) => u.user_id),
            };

            const res = await fetch(`${API_BASE_URL}/api/groups`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(payload),
            });

            const data = await res.json();

            if (!res.ok) {
                throw new Error(data.message || "Failed to create group");
            }

            const newGroup = {
                ...data.group,
                role: "admin",
                members: [currentUser, ...selectedUsers],
            };
            setGroups([...groups, newGroup]);
            resetGroupModal();
            setShowSuccess(true);
            setTimeout(() => setShowSuccess(false), 3000);
        } catch (err) {
            console.error("Error creating group:", err);
            toast.error(err.message || "Failed to create group");
        } finally {
            setIsLoading(false);
        }
    };

    const resetGroupModal = useCallback(() => {
        setGroupName("");
        setGroupDescription("");
        setSelectedUsers([]);
        setShowGroupModal(false);
        setEditingGroup(null);
    }, []);

    const handleSelectUser = useCallback((user) => {
        setSelectedUsers(prev => {
            if (!prev.find((u) => u.user_id === user.user_id)) {
                return [...prev, user];
            }
            return prev;
        });
        setSearchTerm("");
    }, []);

    const handleRemoveUser = useCallback((id) => {
        setSelectedUsers(prev => prev.filter((u) => u.user_id !== id));
    }, []);

    // Memoized filtered users
    const filteredUsers = useMemo(() => {
        return users.filter((u) =>
            // exclude current user
            u.user_id !== currentUser?.user_id &&

            // exclude already selected users
            !selectedUsers.find(su => su.user_id === u.user_id) &&

            // same class/yearGroup
            u.yearGroup === currentUser?.yearGroup &&

            // match search term (firstName, lastName, or email)
            (
                (u.firstName && u.firstName.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (u.lastName && u.lastName.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (u.email && u.email.toLowerCase().includes(searchTerm.toLowerCase()))
            )
        );
    }, [users, currentUser, selectedUsers, searchTerm]);


    // console.log(users, filteredUsers)
    // Memoized stats calculations
    const stats = useMemo(() => {
        const totalGroups = groups.length;
        const adminGroups = groups.filter(g => g.role === "admin").length;
        const totalExams = exams.length;
        const upcomingExams = exams.filter(e => new Date(e.startTime) > now).length;

        return { totalGroups, adminGroups, totalExams, upcomingExams };
    }, [groups, exams, now]);

    const UserAvatar = React.memo(({ user, size = "sm" }) => {
        const sizeClasses = {
            xs: "w-6 h-6 text-xs",
            sm: "w-8 h-8 text-sm",
            md: "w-10 h-10 text-base",
            lg: "w-12 h-12 text-lg"
        };

        return (
            <div className={`${sizeClasses[size]} rounded-full bg-gradient-to-br from-blue-400 to-purple-600 text-white flex items-center justify-center font-semibold`}>
                {user.avatar || `${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`}
            </div>
        );
    });

    const GroupCard = React.memo(({ group }) => {
        const navigate = useNavigate();
        const [groupExams, setGroupExams] = useState([]);
        const [loading, setLoading] = useState(false);
        const [updatingStatus, setUpdatingStatus] = useState(false);
        const [status, setStatus] = useState(group.status); // ✅ local status state
        const [showAllExams, setShowAllExams] = useState(false);

        useEffect(() => {
            setStatus(group.status); // ensure re-sync if prop changes
        }, [group.status]);

        useEffect(() => {
            const fetchExams = async () => {
                try {
                    setLoading(true);
                    const res = await fetch(
                        `${API_BASE_URL}/api/group-exams/user/group/${group.group_id}`,
                        {
                            headers: { Authorization: `Bearer ${token}` },
                        }
                    );

                    const data = await res.json();
                    if (res.ok && data.exams) {
                        setGroupExams(data.exams);
                    } else {
                        console.error(data.message || "Failed to fetch exams for group");
                    }
                } catch (err) {
                    console.error("Error fetching exams:", err);
                } finally {
                    setLoading(false);
                }
            };

            if (token && group.group_id) {
                fetchExams();
            }
        }, [group.group_id, token, API_BASE_URL]);

        // ✅ Update membership status
        const handleStatusUpdate = async (action) => {
            try {
                setUpdatingStatus(true);
                const res = await fetch(`${API_BASE_URL}/api/groups/${group.group_id}/status`, {
                    method: "PATCH",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({ action }), // ✅ send 'action' instead of 'status'
                });

                const data = await res.json();
                if (res.ok) {
                    setStatus(action); // update UI locally
                } else {
                    console.error(data.message || "Failed to update status");
                }
            } catch (error) {
                console.error("Error updating status:", error);
            } finally {
                setUpdatingStatus(false);
            }
        };


        const totalExams = groupExams.length;
        const upcomingExams = groupExams.filter(
            (exam) => new Date(exam.startTime) > new Date()
        ).length;

        const displayedExams = showAllExams ? groupExams : groupExams.slice(0, 1);

        const getStatusBadge = (status) => {
            switch (status) {
                case "accepted":
                    return <span className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded-full">Active</span>;
                case "pending":
                    return <span className="px-2 py-1 text-xs bg-yellow-100 text-yellow-700 rounded-full">Pending</span>;
                case "rejected":
                    return <span className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded-full">Rejected</span>;
                default:
                    return null;
            }
        };

        const getExamStatus = (exam) => {
            const now = new Date();
            const startTime = new Date(exam.startTime);
            const endTime = new Date(startTime.getTime() + exam.availableDuration * 3600000); // hours not minutes

            // console.log('start:', startTime, '| end:', endTime, '| now:', now);

            if (now < startTime) {
                return { text: 'Upcoming', color: 'text-blue-600 bg-blue-50' };
            }
            if (now >= startTime && now <= endTime) {
                return { text: 'Active', color: 'text-green-600 bg-green-50' };
            }
            return { text: 'Ended', color: 'text-gray-600 bg-gray-50' };
        };





        const formatDateTime = (dateString) => {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        };

        const handleExamClick = (exam) => {
            const status = getExamStatus(exam);
            if (status.text === 'Active' || status.text === 'Upcoming') {
                navigate(`/take-exam?subject=${encodeURIComponent(exam.subject)}&examId=${exam.exam_id}`);
            }
        };

        return (
            <div className="bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-100 overflow-hidden">
                <div className="p-6">
                    {/* Header Section */}
                    <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold text-lg shadow-md">
                                {group.groupName?.charAt(0).toUpperCase() || "G"}
                            </div>
                            <div>
                                <h3 className="font-semibold text-lg text-gray-900 flex items-center gap-2">
                                    {group.groupName || "Unnamed Group"}
                                    {group.role === "admin" && (
                                        <Crown className="w-4 h-4 text-yellow-500" />
                                    )}
                                </h3>
                                <div className="flex items-center gap-2 mt-1">
                                    {getStatusBadge(status)}
                                    {status === "pending" && (
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => handleStatusUpdate("accepted")}
                                                disabled={updatingStatus}
                                                className="flex items-center gap-1 text-xs px-2 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 transition-all"
                                            >
                                                {updatingStatus ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3" />}
                                                Accept
                                            </button>
                                            <button
                                                onClick={() => handleStatusUpdate("rejected")}
                                                disabled={updatingStatus}
                                                className="flex items-center gap-1 text-xs px-2 py-1 bg-red-600 text-white rounded-md hover:bg-red-700 transition-all"
                                            >
                                                {updatingStatus ? <Loader2 className="w-3 h-3 animate-spin" /> : <XCircle className="w-3 h-3" />}
                                                Reject
                                            </button>
                                        </div>
                                    )}
                                </div>
                                <p className="text-sm text-gray-500">
                                    {group.members?.length || 0} members
                                </p>
                            </div>
                        </div>
                        <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors opacity-0 group-hover:opacity-100">
                            <MoreVertical className="w-4 h-4 text-gray-500" />
                        </button>
                    </div>

                    {/* Description */}
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                        {group.description || "No description provided"}
                    </p>

                    {/* Stats */}
                    <div className="flex items-center gap-4 mb-4 pb-4 border-b border-gray-100">
                        <div className="flex items-center gap-1.5 text-sm text-gray-600">
                            <BookOpen className="w-4 h-4" />
                            <span className="font-medium">{totalExams}</span> exams
                        </div>
                        <div className="flex items-center gap-1.5 text-sm text-purple-600 font-medium">
                            <CalendarIcon className="w-4 h-4" />
                            <span className="font-semibold">{upcomingExams}</span> upcoming
                        </div>
                        <div className="flex items-center gap-1.5 text-sm text-gray-500">
                            <Calendar className="w-4 h-4" />
                            {group.created_at
                                ? new Date(group.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                                : "Unknown"}
                        </div>
                    </div>

                    {/* Exams List */}
                    {loading ? (
                        <div className="flex items-center justify-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-2 border-indigo-600 border-t-transparent"></div>
                        </div>
                    ) : groupExams.length > 0 ? (
                        <div className="mb-4">
                            <div className="space-y-2">
                                {displayedExams.map((exam) => {
                                    const status = getExamStatus(exam);
                                    const isClickable = status.text === 'Active' || status.text === 'Upcoming';

                                    return (
                                        <div
                                            key={exam.exam_id}
                                            onClick={() => isClickable && handleExamClick(exam)}
                                            className={`p-3 bg-gray-50 rounded-lg transition-all border border-gray-200 ${isClickable
                                                ? 'hover:bg-indigo-50 hover:border-indigo-200 cursor-pointer hover:shadow-sm'
                                                : 'opacity-60 cursor-not-allowed'
                                                }`}
                                        >
                                            <div className="flex items-start justify-between">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <h4 className="font-medium text-sm text-gray-900">
                                                            {exam.title}
                                                        </h4>
                                                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${status.color}`}>
                                                            {status.text}
                                                        </span>
                                                    </div>
                                                    <p className="text-xs text-indigo-600 font-medium mb-2">
                                                        {exam.subject}
                                                    </p>
                                                    <div className="flex items-center gap-3 text-xs text-gray-500">
                                                        <span className="flex items-center gap-1">
                                                            <Clock className="w-3 h-3" />
                                                            {exam.duration} mins
                                                        </span>
                                                        <span className="flex items-center gap-1">
                                                            <FileText className="w-3 h-3" />
                                                            {exam.numberOfQuestions} qs
                                                        </span>
                                                        <span className="flex items-center gap-1">
                                                            <Calendar className="w-3 h-3" />
                                                            {formatDateTime(exam.startTime)}
                                                        </span>
                                                    </div>
                                                </div>
                                                {isClickable && (
                                                    <ExternalLink className="w-4 h-4 text-gray-400 ml-2 flex-shrink-0" />
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {groupExams.length > 1 && (
                                <button
                                    onClick={() => setShowAllExams(!showAllExams)}
                                    className="mt-3 text-sm text-indigo-600 hover:text-indigo-700 font-medium flex items-center gap-1 transition-colors"
                                >
                                    {showAllExams ? (
                                        <>
                                            Show less
                                            <ChevronUp className="w-4 h-4" />
                                        </>
                                    ) : (
                                        <>
                                            Show all {groupExams.length} exams
                                            <ChevronDown className="w-4 h-4" />
                                        </>
                                    )}
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className="py-8 text-center">
                            <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                            <p className="text-sm text-gray-500">No exams available yet</p>
                        </div>
                    )}

                    {/* Footer */}
                    <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                        <div className="flex -space-x-2">
                            {group.members?.slice(0, 4).map((member) => (
                                <div key={member.user_id || member.id} className="relative">
                                    <UserAvatar user={member} size="sm" />
                                    {member.role === "admin" && (
                                        <div className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-400 rounded-full border-2 border-white"></div>
                                    )}
                                </div>
                            ))}
                            {group.members?.length > 4 && (
                                <div className="w-8 h-8 rounded-full bg-gray-200 text-gray-600 flex items-center justify-center text-xs font-medium border-2 border-white">
                                    +{group.members.length - 4}
                                </div>
                            )}
                        </div>

                        <button
                            onClick={() => {
                                if (group.role === "admin") {
                                    navigate(`/groups/${group.group_id}`);
                                } else {
                                    setSelectedGroup(group);
                                }
                            }}
                            className="px-4 py-2 rounded-lg text-sm font-medium transition-all bg-indigo-600 text-white hover:bg-indigo-700 shadow-sm hover:shadow"
                        >
                            {group.role === "admin" ? "Manage" : "View Details"}
                        </button>
                    </div>
                </div>
            </div>
        );
    });

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
            {showSuccess && (
                <div className="fixed top-4 right-4 z-50 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-2 animate-in slide-in-from-top-2">
                    <CheckCircle className="w-5 h-5" />
                    Action completed successfully!
                </div>
            )}

            <div className="max-w-7xl mx-auto p-6 space-y-8">
                <header className="flex flex-col md:flex-row items-center justify-between mb-10 px-4 md:px-0 gap-6">
                    <div className="flex-shrink-0">
                        <button
                            onClick={() => navigate(-1)}
                            className="flex items-center text-blue-600 hover:text-blue-800 transition-colors font-medium"
                        >
                            <ArrowLeft className="w-5 h-5 mr-2" /> Back
                        </button>
                    </div>

                    <div className="flex-1 text-center">
                        <h1 className="text-3xl font-bold text-gray-900 flex items-center justify-center gap-4">
                            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
                                <Layers className="w-6 h-6 text-white" />
                            </div>
                            Groups & Exams
                        </h1>
                        <p className="text-gray-600 text-sm md:text-base mt-1">
                            Manage your study groups and schedule exams
                        </p>
                    </div>

                    <div className="flex-shrink-0">
                        <button
                            onClick={() => setShowGroupModal(true)}
                            className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-3 rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all shadow-lg hover:shadow-xl transform hover:scale-105 whitespace-nowrap"
                        >
                            <PlusCircle className="w-5 h-5" />
                            Create Group
                        </button>
                    </div>
                </header>

                <section className="grid grid-cols-1 md:grid-cols-4 gap-6">
                    {[
                        { title: "Total Groups", value: stats.totalGroups, icon: <Users className="w-6 h-6 text-blue-600" />, bg: "bg-blue-100" },
                        { title: "Admin Groups", value: stats.adminGroups, icon: <Crown className="w-6 h-6 text-yellow-600" />, bg: "bg-yellow-100" },
                        { title: "Total Exams", value: stats.totalExams, icon: <BookOpen className="w-6 h-6 text-green-600" />, bg: "bg-green-100" },
                        { title: "Upcoming Exams", value: stats.upcomingExams, icon: <Calendar className="w-6 h-6 text-purple-600" />, bg: "bg-purple-100" },
                    ].map((card, index) => (
                        <div key={index} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600">{card.title}</p>
                                <p className="text-2xl font-bold text-gray-900">{card.value}</p>
                            </div>
                            <div className={`w-12 h-12 ${card.bg} rounded-xl flex items-center justify-center`}>
                                {card.icon}
                            </div>
                        </div>
                    ))}
                </section>

                <section className="flex flex-col md:flex-row items-center justify-between gap-4">
                    <div className="flex items-center gap-4 flex-1">
                        <div className="relative flex-1">
                            <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search groups..."
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>

                        <select
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                            className="px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                            <option value="all">All Groups</option>
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                        </select>
                    </div>

                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setViewMode("grid")}
                            className={`p-2 rounded-lg ${viewMode === "grid" ? "bg-blue-100 text-blue-600" : "text-gray-400 hover:text-gray-600"}`}
                        >
                            <Layers className="w-5 h-5" />
                        </button>
                        <button
                            onClick={() => setViewMode("list")}
                            className={`p-2 rounded-lg ${viewMode === "list" ? "bg-blue-100 text-blue-600" : "text-gray-400 hover:text-gray-600"}`}
                        >
                            <Users className="w-5 h-5" />
                        </button>
                    </div>
                </section>

                <section>
                    {filteredGroups.length === 0 ? (
                        <div className="bg-white rounded-2xl p-12 text-center shadow-sm border border-gray-100">
                            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Users className="w-8 h-8 text-gray-400" />
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">No groups found</h3>
                            <p className="text-gray-600 mb-6">Get started by creating your first study group</p>
                            <button
                                onClick={() => setShowGroupModal(true)}
                                className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-xl hover:bg-blue-700 transition-colors"
                            >
                                <PlusCircle className="w-5 h-5" />
                                Create Your First Group
                            </button>
                        </div>
                    ) : (
                        <div className={viewMode === "grid" ? "grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6" : "space-y-4"}>
                            {filteredGroups.map((group) => (
                                <GroupCard key={group.group_id} group={group} />
                            ))}
                        </div>
                    )}
                </section>
            </div>

            {showGroupModal && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
                    <div className="bg-white rounded-2xl p-8 w-full max-w-2xl max-h-[90vh] overflow-y-auto mx-4">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-2xl font-bold flex items-center gap-3">
                                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                                    <Users className="w-5 h-5 text-blue-600" />
                                </div>
                                {editingGroup ? "Edit Group" : "Create New Group"}
                            </h2>
                            <button
                                onClick={resetGroupModal}
                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="space-y-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Group Name *
                                </label>
                                <input
                                    type="text"
                                    placeholder="Enter a descriptive group name"
                                    value={groupName}
                                    onChange={(e) => setGroupName(e.target.value)}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Description
                                </label>
                                <textarea
                                    placeholder="Describe the purpose and goals of this group"
                                    value={groupDescription}
                                    onChange={(e) => setGroupDescription(e.target.value)}
                                    rows={3}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Add Members
                                </label>
                                <div className="relative">
                                    <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                    <input
                                        type="text"
                                        placeholder="Search users by name or email"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>

                                {searchTerm && (
                                    <div className="mt-2 border border-gray-200 rounded-xl shadow-lg bg-white max-h-48 overflow-y-auto">
                                        {filteredUsers.length > 0 ? (
                                            filteredUsers.map((user) => (
                                                <button
                                                    key={user.user_id}
                                                    onClick={() => handleSelectUser(user)}
                                                    className="w-full px-4 py-3 hover:bg-gray-50 flex items-center gap-3 text-left transition-colors"
                                                >
                                                    <UserAvatar user={user} size="sm" />
                                                    <div>
                                                        <p className="font-medium text-gray-900">
                                                            {user.firstName} {user.lastName}
                                                        </p>
                                                        <p className="text-sm text-gray-500">{user.email}</p>
                                                    </div>
                                                </button>
                                            ))
                                        ) : (
                                            <div className="px-4 py-3 text-gray-500 text-center">
                                                No users found
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {selectedUsers.length > 0 && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Selected Members ({selectedUsers.length})
                                    </label>
                                    <div className="space-y-2 max-h-32 overflow-y-auto">
                                        {selectedUsers.map((user) => (
                                            <div
                                                key={user.user_id}
                                                className="flex items-center justify-between p-3 bg-blue-50 rounded-xl"
                                            >
                                                <div className="flex items-center gap-3">
                                                    <UserAvatar user={user} size="sm" />
                                                    <div>
                                                        <p className="font-medium text-gray-900">
                                                            {user.firstName} {user.lastName}
                                                        </p>
                                                        <p className="text-sm text-gray-600">{user.email}</p>
                                                    </div>
                                                </div>
                                                <button
                                                    onClick={() => handleRemoveUser(user.user_id)}
                                                    className="p-1 hover:bg-red-100 rounded-lg text-red-600 transition-colors"
                                                >
                                                    <X className="w-4 h-4" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="flex justify-end gap-3 mt-8">
                            <button
                                onClick={resetGroupModal}
                                className="px-6 py-3 text-gray-700 border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleCreateGroup}
                                disabled={isLoading || !groupName.trim()}
                                className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                            >
                                {isLoading && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                                {editingGroup ? "Update Group" : "Create Group"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}